<template>
  <main class="academy-page">
    <div class="academy-shell">
      <MarketingToolHeader
        class="academy-header"
        eyebrow="Well come FIT Akademie"
        :title="welcomeTitle"
        :description="headerDescription"
        icon="mdi-school-outline"
        compact-on-mobile>
        <template v-slot:actions>
          <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zum Dashboard" title="Zurück zum Dashboard" @click="goBack">
            <v-icon>mdi-view-dashboard-outline</v-icon>
          </v-btn>
          <v-btn v-if="isTeacher" depressed class="academy-role-action academy-role-action--teacher" to="/akademie-teacher">
            <v-icon>mdi-account-tie-outline</v-icon>
            <span>Teacher</span>
          </v-btn>
          <v-btn v-if="isAdmin" depressed class="academy-role-action academy-role-action--admin" to="/akademie-admin">
            <v-icon>mdi-shield-crown-outline</v-icon>
            <span>Admin</span>
          </v-btn>
          <v-btn v-if="isLeadershipAdmin" depressed class="academy-role-action academy-role-action--leadership" to="/akademie-leadership-admin">
            <v-icon>mdi-shield-account-outline</v-icon>
            <span>Leadership Admin</span>
          </v-btn>
        </template>
      </MarketingToolHeader>

      <aside
        v-if="showWelcomeModuleNotice"
        class="welcome-module-notice"
        aria-labelledby="welcome-module-notice-title">
        <span class="welcome-module-icon" aria-hidden="true">
          <v-icon>mdi-school-outline</v-icon>
        </span>
        <div class="welcome-module-copy">
          <div class="welcome-module-eyebrow">Dein erster Schritt</div>
          <h2 id="welcome-module-notice-title">{{ welcomeModule.title }}</h2>
          <p>
            Dieses kurze Einstiegsmodul zeigt dir, wie die Academy dich im Arbeitsalltag unterstützt und welche Ziele wir gemeinsam verfolgen. Bitte
            schliesse es zuerst ab.
          </p>
          <span class="welcome-module-meta">
            <v-icon aria-hidden="true">mdi-clock-outline</v-icon>
            Ca. {{ welcomeModule.estimatedMinutes || 2 }} Min
          </span>
        </div>
        <v-btn depressed class="welcome-module-action" :to="welcomeModulePath">
          {{ welcomeModuleActionLabel }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </aside>

      <section v-if="hasClassicAccess" class="metric-grid" aria-label="Deine Akademie Übersicht">
        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-green"><v-icon>mdi-check-decagram-outline</v-icon></span>
          <div>
            <span>Abgeschlossen</span>
            <strong>{{ completedModuleCount }}</strong>
            <small>Module erledigt</small>
          </div>
        </v-card>

        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-license"><v-icon>mdi-medal-outline</v-icon></span>
          <div>
            <span>Aktive Lizenzen</span>
            <strong>{{ activeLicenseCount }}</strong>
            <small class="license-summary" aria-label="Lizenzstufen">
              <span
                v-for="tier in licenseTiers"
                :key="tier.key"
                class="license-dot"
                :class="{ active: hasLicense(tier.key) }"
                :style="{ '--tier-color': tier.color }">
                {{ tier.shortLabel }}
              </span>
            </small>
          </div>
        </v-card>

        <v-card dark flat class="metric-card">
          <span class="metric-icon icon-calendar"><v-icon>mdi-calendar-star-outline</v-icon></span>
          <div>
            <span>Offene Schulungen</span>
            <strong>{{ openSessionCount }}</strong>
            <small>aktuell buchbar</small>
          </div>
        </v-card>

        <v-card dark flat class="metric-card">
          <span class="metric-icon"><v-icon>mdi-calendar-check-outline</v-icon></span>
          <div>
            <span>Deine Termine</span>
            <strong>{{ myUpcoming.length }}</strong>
            <small>kommende Anmeldungen</small>
          </div>
        </v-card>
      </section>

      <v-card v-if="myUpcoming.length" dark flat class="upcoming-surface">
        <div class="surface-header">
          <div>
            <div class="surface-eyebrow">Dein Plan</div>
            <h2>Nächste Schulungen</h2>
            <p>Deine Anmeldungen und Wartelistenplätze auf einen Blick.</p>
          </div>
        </div>

        <div class="upcoming-scroll">
          <button
            v-for="session in myUpcoming"
            :key="session._id"
            type="button"
            class="upcoming-card"
            @click="$router.push('/akademie/schulungen/' + session._id)">
            <span class="date-tile">
              <strong>{{ formatDay(session.startsAt) }}</strong>
              <small>{{ formatMonthShort(session.startsAt) }}</small>
            </span>
            <span class="upcoming-copy">
              <strong>{{ session.title }}</strong>
              <small>
                <v-icon>mdi-clock-outline</v-icon>
                {{ formatTime(session.startsAt) }}
                <template v-if="session.location"> · {{ session.location }}</template>
              </small>
              <span
                class="registration-status"
                :class="session.myRegistration && session.myRegistration.status === 'waiting_list' ? 'is-waiting' : 'is-confirmed'">
                {{ session.myRegistration && session.myRegistration.status === "waiting_list" ? "Warteliste" : "Angemeldet" }}
              </span>
            </span>
            <v-icon class="upcoming-arrow">mdi-chevron-right</v-icon>
          </button>
        </div>
      </v-card>

      <nav class="academy-nav" aria-label="Akademie Bereiche">
        <button
          v-for="item in visibleNavItems"
          :key="item.path"
          type="button"
          class="nav-item"
          :class="{ active: isActiveRoute(item.path) }"
          :aria-current="isActiveRoute(item.path) ? 'page' : null"
          @click="navigateToNavItem(item)">
          <span class="nav-icon"
            ><v-icon>{{ item.icon }}</v-icon></span
          >
          <span class="nav-copy">
            <strong>{{ item.label }}</strong>
            <small>{{ item.description }}</small>
          </span>
          <span v-if="item.path === '/akademie/schulungen' && openSessionCount > 0" class="nav-count">{{ openSessionCount }}</span>
          <v-icon v-else class="nav-arrow">mdi-chevron-right</v-icon>
        </button>
      </nav>

      <section class="academy-view">
        <v-fade-transition mode="out-in">
          <router-view></router-view>
        </v-fade-transition>
      </section>
    </div>
  </main>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

const WELCOME_MODULE_ID = "6a85c343171063bff07f6151"

export default {
  name: "AkademieMain",
  components: { MarketingToolHeader },
  data() {
    return {
      userService,
      myLicenses: [],
      sessions: [],
      progressEntries: [],
      progressLoaded: false,
      welcomeModule: null,
      licenseTiers: [
        { key: "bronze", shortLabel: "B", color: "#cd7f32" },
        { key: "silber", shortLabel: "S", color: "#c0c0c0" },
        { key: "gold", shortLabel: "G", color: "#ffd700" },
      ],
      navItems: [
        {
          path: "/akademie/suite",
          icon: "mdi-book-open-page-variant-outline",
          label: "Learning Suite",
          description: "Module & Fortschritt",
          classicOnly: true,
        },
        {
          path: "/akademie/guidelines",
          icon: "mdi-bookshelf",
          label: "Guidelines",
          description: "Leitfäden & Dokumente",
          classicOnly: true,
        },
        {
          path: "/akademie/kreativwerkstatt",
          icon: "mdi-lightbulb-on-outline",
          label: "Kreativwerkstatt",
          description: "Ideen & Austausch",
          classicOnly: true,
        },
        {
          path: "/akademie/schulungen",
          icon: "mdi-calendar-star-outline",
          label: "Schulungsplan",
          description: "Termine & Anmeldung",
          classicOnly: true,
        },
        {
          path: "/akademie/leadership",
          icon: "mdi-account-tie-outline",
          label: "Leadership",
          description: "Connect, Coaching & 1:1",
          leadershipOnly: true,
        },
      ],
    }
  },
  computed: {
    userFirstName() {
      return this.userService.getUserFirstName()
    },
    welcomeTitle() {
      return this.userFirstName ? `Willkommen, ${this.userFirstName}!` : "Willkommen in der Akademie!"
    },
    headerDescription() {
      if (!this.hasClassicAccess && this.hasLeadershipAccess) {
        return "Dein geschützter Bereich für Leadership Connect, praxisnahes Coaching und individuelle 1:1-Sessions."
      }
      if (this.hasClassicAccess && this.hasLeadershipAccess) {
        return "Dein Ort für Weiterbildung, Leadership, Austausch und kommende Schulungen. Behalte deine Lernwege und Termine im Blick."
      }
      return "Dein Ort für Weiterbildung, Austausch und kommende Schulungen. Lerne in deinem Tempo und behalte deinen Fortschritt im Blick."
    },
    isTeacher() {
      return this.userService.hasRight("aka_teacher", "aka_manage", "aka_leadership_manage")
    },
    isAdmin() {
      return this.userService.hasRight("aka_manage")
    },
    hasLeadershipAccess() {
      return this.userService.hasRight("aka_leadership") || this.userService.hasRight("aka_leadership_manage")
    },
    hasClassicAccess() {
      return this.userService.hasRight("aka_fetch", "aka_teacher", "aka_manage")
    },
    isLeadershipAdmin() {
      return this.userService.hasRight("aka_leadership_manage")
    },
    welcomeModulePath() {
      return `/akademie/modules/${WELCOME_MODULE_ID}`
    },
    welcomeModuleProgress() {
      return (
        this.progressEntries.find(
          (progress) => progress.module === WELCOME_MODULE_ID || (progress.module && progress.module._id === WELCOME_MODULE_ID),
        ) || null
      )
    },
    showWelcomeModuleNotice() {
      return Boolean(
        this.hasClassicAccess &&
          this.progressLoaded &&
          this.welcomeModule &&
          this.welcomeModule.active !== false &&
          !(this.welcomeModuleProgress && this.welcomeModuleProgress.completedAt),
      )
    },
    welcomeModuleActionLabel() {
      return this.welcomeModuleProgress ? "Weiterlernen" : "Modul starten"
    },
    visibleNavItems() {
      return this.navItems.filter((item) => (!item.leadershipOnly || this.hasLeadershipAccess) && (!item.classicOnly || this.hasClassicAccess))
    },
    activeLicenseCount() {
      return this.myLicenses.filter((license) => ["active", "expiring_soon"].includes(license.status)).length
    },
    openSessionCount() {
      const now = new Date()
      return this.sessions.filter((session) => {
        if (!session.registrationEnd || !session.startsAt) return false
        return new Date(session.registrationEnd) >= now && new Date(session.startsAt) >= now
      }).length
    },
    completedModuleCount() {
      return this.progressEntries.filter((progress) => progress.completedAt).length
    },
    myUpcoming() {
      const now = new Date()
      return this.sessions
        .filter(
          (session) =>
            session.myRegistration &&
            ["registered", "waiting_list"].includes(session.myRegistration.status) &&
            session.startsAt &&
            new Date(session.startsAt) >= now,
        )
        .sort((first, second) => new Date(first.startsAt) - new Date(second.startsAt))
        .slice(0, 6)
    },
  },
  mounted() {
    this.loadOverview()
  },
  methods: {
    navigateToNavItem(item) {
      this.$router.push(item.path)
    },
    isActiveRoute(path) {
      return Boolean(this.$route && this.$route.path.startsWith(path))
    },
    goBack() {
      this.$router.push("/dashboard")
    },
    formatDay(date) {
      return date ? new Date(date).getDate() : ""
    },
    formatMonthShort(date) {
      return date ? new Date(date).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase() : ""
    },
    formatTime(date) {
      return date ? new Date(date).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) : ""
    },
    async loadOverview() {
      if (!this.hasClassicAccess) return
      try {
        const [licenses, sessions, progressEntries, welcomeModule] = await Promise.all([
          api.akademie.fetchMyLicenses().catch(() => []),
          api.akademie.fetchTermine().catch(() => []),
          api.akademie.fetchProgress({ track: "license" }).catch(() => null),
          api.akademie.fetchModule(WELCOME_MODULE_ID).catch(() => null),
        ])
        this.myLicenses = licenses || []
        this.sessions = sessions || []
        this.progressLoaded = Array.isArray(progressEntries)
        this.progressEntries = progressEntries || []
        this.welcomeModule = welcomeModule
      } catch (error) {
        console.error("Akademie Daten konnten nicht geladen werden", error)
      }
    },
    hasLicense(tier) {
      return this.myLicenses.some((license) => license.tier === tier && ["active", "expiring_soon"].includes(license.status))
    },
  },
}
</script>

<style scoped>
.academy-page {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.academy-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.primary-action,
.secondary-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-decoration: none !important;
  text-transform: none;
}

.academy-role-action:hover,
.academy-role-action:focus,
.academy-role-action:active {
  text-decoration: none !important;
}

.primary-action {
  color: #ffffff !important;
}

.academy-role-action {
  --role-color: #7ee5a6;
  --role-border: rgba(88, 211, 136, 0.34);
  --role-background: rgba(0, 150, 64, 0.1);
  min-height: 2.75rem !important;
  padding: 0 0.875rem !important;
  border: 1px solid var(--role-border) !important;
  border-radius: 0.8125rem !important;
  color: var(--role-color) !important;
  background: var(--role-background) !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04) !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.academy-role-action--admin {
  --role-color: #f4d06f;
  --role-border: rgba(244, 208, 111, 0.3);
  --role-background: rgba(244, 201, 93, 0.09);
}

.academy-role-action--leadership {
  --role-color: #c4b5fd;
  --role-border: rgba(167, 139, 250, 0.32);
  --role-background: rgba(139, 92, 246, 0.1);
}

.academy-role-action ::v-deep .v-btn__content {
  gap: 0.4375rem;
}

.academy-role-action ::v-deep .v-icon {
  color: currentColor !important;
}

.welcome-module-notice {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
  padding: 1rem 1.125rem;
  border: 1px solid rgba(88, 211, 136, 0.34);
  border-radius: 1.1875rem;
  background:
    radial-gradient(circle at 85% 0%, rgba(88, 211, 136, 0.13), transparent 18rem),
    linear-gradient(120deg, rgba(0, 150, 64, 0.14), rgba(255, 255, 255, 0.035));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.045);
}

.welcome-module-icon {
  display: grid;
  width: 3rem;
  height: 3rem;
  place-items: center;
  border: 1px solid rgba(88, 211, 136, 0.3);
  border-radius: 0.9375rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.16);
}

.welcome-module-icon .v-icon {
  color: currentColor;
  font-size: 1.5rem;
}

.welcome-module-copy {
  min-width: 0;
}

.welcome-module-eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  line-height: 1.3;
  text-transform: uppercase;
}

.welcome-module-copy h2 {
  margin: 0.1875rem 0 0;
  color: #ffffff;
  font-size: 1.125rem;
  line-height: 1.3;
}

.welcome-module-copy p {
  max-width: 56rem;
  margin: 0.375rem 0 0;
  color: #d4d4d8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.welcome-module-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
  margin-top: 0.5rem;
  color: #a3a3a8;
  font-size: 0.75rem;
  font-weight: 700;
}

.welcome-module-meta .v-icon {
  color: #7ee5a6;
  font-size: 1rem;
}

.welcome-module-action {
  min-width: 10.5rem !important;
  min-height: 2.875rem !important;
  padding: 0 1rem !important;
  border-radius: 0.8125rem !important;
  color: #07140c !important;
  background: #7ee5a6 !important;
  font-size: 0.875rem !important;
  font-weight: 800;
  letter-spacing: 0;
  text-decoration: none !important;
  text-transform: none;
}

.welcome-module-action .v-icon {
  color: currentColor !important;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 1rem 0;
}

.metric-card {
  display: flex;
  min-height: 6.75rem;
  align-items: center;
  gap: 0.875rem;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.1875rem !important;
  background: var(--surface) !important;
}

.metric-icon {
  display: grid;
  flex: 0 0 2.625rem;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.metric-icon .v-icon {
  color: currentColor;
}

.icon-green {
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.icon-license {
  color: #f4c95d;
  background: rgba(244, 201, 93, 0.11);
}

.icon-calendar {
  color: #7dd3fc;
  background: rgba(14, 165, 233, 0.11);
}

.metric-card > div {
  display: grid;
  min-width: 0;
}

.metric-card > div > span:first-child {
  color: var(--text-soft);
  font-size: 0.875rem;
  font-weight: 700;
}

.metric-card strong {
  color: #ffffff;
  font-size: 1.6875rem;
  line-height: 1.15;
}

.metric-card small {
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.license-summary {
  display: flex;
  gap: 0.25rem;
}

.license-dot {
  display: inline-grid;
  width: 1.25rem;
  height: 1.25rem;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: #77777d;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.75rem;
  font-weight: 800;
}

.license-dot.active {
  border-color: var(--tier-color);
  color: var(--tier-color);
  background: rgba(255, 255, 255, 0.08);
}

.upcoming-surface {
  margin-bottom: 1rem;
  padding: 1.375rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.surface-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.surface-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.surface-header h2 {
  margin: 0.1875rem 0 0;
  color: var(--text);
  font-size: 1.25rem;
  line-height: 1.3;
}

.surface-header p {
  margin: 0.25rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.upcoming-scroll {
  display: flex;
  gap: 0.75rem;
  padding: 0.125rem 0 0.375rem;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-color: rgba(88, 211, 136, 0.35) transparent;
  scrollbar-width: thin;
  scroll-snap-type: x proximity;
}

.upcoming-card {
  display: grid;
  min-width: min(19rem, 82vw);
  max-width: 22rem;
  grid-template-columns: 3.25rem minmax(0, 1fr) 1.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid var(--border);
  border-radius: 1rem;
  outline: none;
  color: var(--text);
  background: var(--surface);
  cursor: pointer;
  scroll-snap-align: start;
  text-align: left;
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.upcoming-card:focus-visible,
.nav-item:focus-visible {
  border-color: rgba(0, 150, 64, 0.68);
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.date-tile {
  display: grid;
  width: 3.25rem;
  min-height: 3.625rem;
  place-content: center;
  border: 1px solid rgba(0, 150, 64, 0.22);
  border-radius: 0.8125rem;
  background: rgba(0, 150, 64, 0.1);
  text-align: center;
}

.date-tile strong {
  font-size: 1.25rem;
  line-height: 1.05;
}

.date-tile small {
  margin-top: 0.1875rem;
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.upcoming-copy {
  display: grid;
  min-width: 0;
}

.upcoming-copy > strong,
.upcoming-copy > small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upcoming-copy > strong {
  font-size: 0.9375rem;
  line-height: 1.35;
}

.upcoming-copy > small {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.1875rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.upcoming-copy > small .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.registration-status {
  width: fit-content;
  margin-top: 0.4375rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 750;
}

.is-confirmed {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.15);
}

.is-waiting {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.15);
}

.upcoming-arrow,
.nav-arrow {
  color: #8f9095 !important;
}

.academy-nav {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 0.625rem;
  margin: 1rem 0;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.026);
}

.nav-item {
  display: grid;
  min-height: 4.5rem;
  grid-template-columns: 2.625rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.9375rem;
  outline: none;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: border-color 160ms ease, background-color 160ms ease, color 160ms ease;
}

.nav-item.active {
  border-color: rgba(0, 150, 64, 0.28);
  color: #ffffff;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.13), rgba(255, 255, 255, 0.035));
}

.nav-icon {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.055);
}

.nav-item.active .nav-icon {
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.nav-icon .v-icon {
  color: currentColor;
}

.nav-copy {
  display: grid;
  min-width: 0;
}

.nav-copy strong {
  font-size: 0.875rem;
  line-height: 1.35;
}

.nav-copy small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
  line-height: 1.35;
}

.nav-count {
  display: inline-grid;
  min-width: 1.75rem;
  min-height: 1.75rem;
  place-items: center;
  padding: 0.1875rem 0.4375rem;
  border-radius: 999px;
  color: #ffffff;
  background: #009640;
  font-size: 0.75rem;
  font-weight: 800;
}

.academy-view {
  min-width: 0;
  padding-top: 0.25rem;
}

@media (hover: hover) and (pointer: fine) {
  .welcome-module-action:hover {
    background: #95edb6 !important;
  }

  .upcoming-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong);
  }

  .nav-item:not(:disabled):hover {
    border-color: var(--border);
    background: rgba(255, 255, 255, 0.04);
  }
}

@media (max-width: 78.9375rem) {
  .academy-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .academy-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .nav-item {
    min-width: 0;
  }

  .nav-item:only-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 37.4375rem) {
  .academy-page {
    padding: 0.75rem;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact {
    align-items: stretch;
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.75rem;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .header-content {
    width: 100%;
    align-items: center;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .header-eyebrow {
    display: block;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .header-copy p {
    display: none;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .header-actions {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(auto-fit, minmax(6.25rem, 1fr));
    gap: 0.5rem;
    margin-left: 0;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .academy-role-action {
    width: 100%;
    height: auto !important;
    min-width: 0 !important;
    min-height: 5rem !important;
    padding: 0.5rem 0.375rem !important;
    border-radius: 0.875rem !important;
    font-size: 0.75rem !important;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .academy-role-action .v-btn__content {
    flex-direction: column;
    gap: 0.25rem;
    line-height: 1.15;
    white-space: normal;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .academy-role-action .v-icon {
    margin: 0 !important;
    font-size: 1.25rem !important;
  }

  .academy-page ::v-deep .academy-header.marketing-tool-header--mobile-compact .academy-role-action span {
    text-align: center;
  }

  .welcome-module-notice {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.75rem;
    padding: 0.875rem;
    border-radius: 1.0625rem;
  }

  .welcome-module-icon {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.8125rem;
  }

  .welcome-module-action {
    width: 100%;
    grid-column: 1 / -1;
    margin-top: 0.125rem;
  }

  .metric-grid {
    gap: 0.5rem;
    margin: 0.75rem 0;
  }

  .metric-card {
    min-height: 5.25rem;
    align-items: center;
    flex-direction: row;
    gap: 0.625rem;
    padding: 0.625rem;
    border-radius: 0.9375rem !important;
  }

  .metric-icon {
    width: 2.125rem;
    height: 2.125rem;
    flex-basis: 2.125rem;
  }

  .metric-card strong {
    font-size: 1.5rem;
  }

  .metric-card small {
    display: none;
  }

  .upcoming-surface {
    padding: 0.875rem;
    border-radius: 1.125rem !important;
  }

  .surface-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 0.875rem;
  }

  .upcoming-scroll {
    display: grid;
    width: 100%;
    gap: 0.625rem;
    padding: 0;
    overflow: visible;
    scroll-snap-type: none;
  }

  .upcoming-card {
    width: 100%;
    min-width: 0;
    max-width: none;
  }

  .upcoming-copy > small {
    line-height: 1.4;
    white-space: normal;
  }

  .academy-nav {
    margin: 0.75rem 0;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 1.0625rem;
  }

  .nav-item {
    position: relative;
    min-height: 5.25rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 2.25rem auto;
    align-content: center;
    align-items: center;
    justify-items: center;
    gap: 0.375rem;
    padding: 0.625rem;
    border-color: rgba(255, 255, 255, 0.055);
    background: rgba(255, 255, 255, 0.025);
    text-align: center;
  }

  .nav-icon {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.6875rem;
  }

  .nav-copy small,
  .nav-arrow {
    display: none;
  }

  .nav-copy {
    width: 100%;
    justify-items: center;
  }

  .nav-copy strong {
    width: 100%;
    line-height: 1.25;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .nav-count {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
  }
}

@media (max-width: 20rem) {
  .metric-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .metric-card {
    min-height: 5.75rem;
    align-items: center;
    flex-direction: row;
  }
}
</style>
